import React, { useState, useCallback } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

interface CopyButtonProps {
  /** The text or object to copy */
  content: unknown;
  /** Tooltip text */
  tooltip?: string;
  /** Size of the button */
  size?: 'small' | 'medium';
}

/**
 * Button to copy content to clipboard with feedback
 */
const CopyButton: React.FC<CopyButtonProps> = ({
  content,
  tooltip = 'Copy to clipboard',
  size = 'small',
}) => {
  const [isCopying, setIsCopying] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (isCopying) return;

    setIsCopying(true);

    try {
      const textToCopy =
        typeof content === 'string'
          ? content
          : JSON.stringify(content, null, 2);

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = textToCopy;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    } finally {
      setIsCopying(false);
    }
  }, [content, isCopying]);

  return (
    <Tooltip title={isCopied ? 'Copied!' : tooltip}>
      <IconButton
        onClick={handleCopy}
        size={size}
        disabled={isCopying}
        sx={{
          color: isCopied ? 'success.main' : 'grey.500',
          '&:hover': {
            color: isCopied ? 'success.main' : 'primary.main',
          },
        }}
      >
        {isCopying ? (
          <CircularProgress size={size === 'small' ? 16 : 20} />
        ) : isCopied ? (
          <CheckIcon fontSize={size} />
        ) : (
          <ContentCopyIcon fontSize={size} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default React.memo(CopyButton);
